import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import { AdCreativeWithUrls } from 'common/api/v1/types'
import { AdCreativesRequestParams } from '../../../../api/nm-types'
import { listAdCreatives } from '../../../../redux/actions/adCreativeActions'
import { AppDispatch, GlobalState, useRoutes } from '../../../../store'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getTransferTableConfig } from '../../adCreative/tableConfig'
import { useUser, withDefaultPagination } from '../../../../utils'
import MuiTable from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { Pagination } from '../../../common/Table'
import Pendable from '../../../common/Pendable'
import DraggableAdCreativeRow from './DraggableAdCreativeRow'
import { FilteredSearchBar } from '../../../common/Filters/FilterView/FilteredSearchBar'
import {
  AdCreativeFilterKey,
  makeAdCreativeFilters,
  mapAdCreativeFilterToUrlParam,
} from '../../adCreative/adCreativeFilter'
import { FilterBy, filtersToUrlParams } from '../../../common/Filters/FilterView/FilterTemplate'
import { AdCreativeRHF } from '../utils'

interface Props {
  adCreatives: AdCreativeRHF[]
  onAdd: (items: AdCreativeWithUrls[]) => void
  onRemove: (items: AdCreativeRHF[]) => void
  onToggleFiller: (items: AdCreativeWithUrls[]) => void
  onReorder: (item: AdCreativeRHF, from: number, to: number) => void
}

const TransferList: React.FC<Props> = ({ adCreatives, onAdd, onRemove, onToggleFiller, onReorder }) => {
  const [checkedLeft, setCheckedLeft] = useState<string[]>([]) // ID for the left panel
  const [checkedRight, setCheckedRight] = useState<string[]>([]) // _locId for the right panel
  const [searchParams, setSearchParams] = useState<AdCreativesRequestParams>(
    withDefaultPagination({} as AdCreativesRequestParams),
  )
  const [appliedFilters, setAppliedFilters] = useState<FilterBy[]>([])

  const user = useUser()
  const routes = useRoutes()

  const [selectedPagination, setSelectedPagination] = useState<{
    pageNumber: string
    rowsPerPage: string
  }>({
    pageNumber: '0',
    rowsPerPage: '10',
  })

  const dispatch = useDispatch<AppDispatch>()
  const { fetchedAdCreatives, totalFetched, loading } = useSelector(
    ({ adCreativeReducer }: GlobalState) => ({
      fetchedAdCreatives: adCreativeReducer.adCreatives,
      totalFetched: adCreativeReducer.total,
      loading: adCreativeReducer.loading,
    }),
    shallowEqual,
  )

  if (fetchedAdCreatives) {
    const withLocId = fetchedAdCreatives as (AdCreativeWithUrls & { locId?: string })[]
    withLocId.forEach((item, index) => {
      item.locId = item.locId || `${index}` // Ensure locId is set for each item
    })
  }

  const refreshAdCreatives = () => {
    dispatch(listAdCreatives(searchParams))
  }

  useEffect(() => {
    refreshAdCreatives()
  }, [dispatch, searchParams])

  const handleToggle = (id: string, side: 'left' | 'right') => {
    const setChecked = side === 'left' ? setCheckedLeft : setCheckedRight
    const checked = side === 'left' ? checkedLeft : checkedRight
    setChecked(checked.includes(id) ? checked.filter((i) => i !== id) : [...checked, id])
  }

  const handleSelectAll = (items: AdCreativeWithUrls[], side: 'left' | 'right') => {
    const allIds = items.map((i) => i.id)
    const checked = side === 'left' ? checkedLeft : checkedRight
    const setChecked = side === 'left' ? setCheckedLeft : setCheckedRight
    const allSelected = allIds.every((id) => checked.includes(id))
    setChecked(
      allSelected
        ? checked.filter((id) => !allIds.includes(id))
        : [...checked, ...allIds.filter((id) => !checked.includes(id))],
    )
  }

  const handleAdd = () => {
    if (!fetchedAdCreatives) return
    const itemsToAdd = fetchedAdCreatives.filter((item) => checkedLeft.includes(item.id))
    onAdd(itemsToAdd)
    setCheckedLeft([])
  }

  const handleRemove = () => {
    const itemsToRemove = adCreatives.filter((item) => checkedRight.includes(item.locId))
    onRemove(itemsToRemove)
    setCheckedRight([])
  }
  const getSlicedAdCreatives = (page: string, rowsPerPage: string) => {
    const start = parseInt(page) * parseInt(rowsPerPage)
    const end = start + parseInt(rowsPerPage)
    return adCreatives.slice(start, end)
  }

  const dataLeft = fetchedAdCreatives || []
  const dataRight = getSlicedAdCreatives(selectedPagination.pageNumber, selectedPagination.rowsPerPage)

  const configLeft = getTransferTableConfig({
    adCreatives: dataLeft,
    selected: checkedLeft,
    handleSelect: (id) => handleToggle(id, 'left'),
    handleSelectAll: () => handleSelectAll(dataLeft, 'left'),
    dragable: false,
    side: 'left',
    user,
    routes,
  }).filter((c) => c !== null)

  const configRight = getTransferTableConfig({
    adCreatives: dataRight,
    selected: checkedRight,
    handleSelect: (id) => handleToggle(id, 'right'),
    handleSelectAll: () => handleSelectAll(dataRight, 'right'),
    dragable: true,
    side: 'right',
    user,
    routes,
  }).filter((c) => c !== null)

  const filters = makeAdCreativeFilters()

  return (
    <Box sx={{ height: '50vh', width: '100%', mt: 2, display: 'flex' }}>
      <Grid container spacing={2} sx={{ height: '100%', flexGrow: 1 }}>
        <Grid
          size={{ xs: 5.5 }}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.paper',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ p: 1, flexShrink: 0 }}>
            Available
          </Typography>
          <Box sx={{ mt: 1, mb: 1 }}>
            <FilteredSearchBar
              appliedFilters={appliedFilters}
              onFiltersApplied={(filters) => {
                setSearchParams((prev) => ({
                  ...filtersToUrlParams(filters, appliedFilters, mapAdCreativeFilterToUrlParam),
                  pageNumber: '0',
                  rowsPerPage: prev.rowsPerPage,
                }))
                setAppliedFilters(filters)
              }}
              searchTokenFilters={filters}
              rawTextFilter={filters.find((f) => f.key === AdCreativeFilterKey.name)!}
            />
          </Box>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', borderRadius: '4px' }}>
            <Pendable pending={loading} cover={!!dataLeft.length} id="available-ad-creatives-table">
              {dataLeft.length > 0 ? (
                <MuiTable id="available-ad-creatives-table">
                  <TableBody>
                    {dataLeft.map((item, i) => (
                      <TableRow hover key={item.id}>
                        {configLeft.map(({ getValue, props, title }) => (
                          <TableCell {...props} data-cell-name={`${title}-cell`} key={`${item.id}-${title}`}>
                            <Typography component="div" variant="body2">
                              {getValue(item as AdCreativeRHF, i)}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </MuiTable>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography>No available creatives.</Typography>
                </Box>
              )}
            </Pendable>
          </Box>
          <Box sx={{ flexShrink: 0, mt: 2 }}>
            <Divider />
            <Pagination
              id="available-ad-creatives-pagination"
              total={totalFetched}
              page={searchParams.pageNumber || '0'}
              perPage={searchParams.rowsPerPage || '10'}
              changePageCallback={(page: string) => {
                setSearchParams((prev) => ({ ...prev, pageNumber: page }))
              }}
              changeRowsPerPageCallback={(rowsPerPage: string) => {
                setSearchParams((prev) => ({ ...prev, pageNumber: '0', rowsPerPage }))
              }}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 1 }} sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Stack spacing={2}>
            <Button
              id="button-transfer-add"
              variant="outlined"
              size="small"
              onClick={handleAdd}
              disabled={!fetchedAdCreatives || checkedLeft.length === 0}
              aria-label="move selected right"
            >
              Add &gt;
            </Button>
            <Button
              id="button-transfer-remove"
              variant="outlined"
              size="small"
              onClick={handleRemove}
              disabled={checkedRight.length === 0}
              aria-label="move selected left"
            >
              &lt; Remove
            </Button>
          </Stack>
        </Grid>
        <Grid
          size={{ xs: 5.5 }}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.paper',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ p: 1, flexShrink: 0 }}>
            Selected
          </Typography>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', borderRadius: '4px' }}>
            {dataRight.length > 0 ? (
              <MuiTable id="selected-ad-creatives-table">
                <TableBody>
                  {dataRight.map((item, i) => (
                    <DraggableAdCreativeRow
                      key={`${item.id}-${i}`}
                      id={item.id}
                      index={i}
                      moveRow={(fromLocal, toLocal) => {
                        const pageOffset =
                          parseInt(selectedPagination.pageNumber) * parseInt(selectedPagination.rowsPerPage)
                        const from = fromLocal + pageOffset
                        const to = toLocal + pageOffset

                        const updated = [...adCreatives]
                        const [movedItem] = updated.splice(from, 1)
                        updated.splice(to, 0, movedItem)

                        const updatedCheckedRight = checkedRight.map((locId) => {
                          const index = parseInt(locId)
                          if (index === from) return `${to}`
                          if (from > to && index >= to && index < from) return `${index + 1}`
                          if (from < to && index > from && index <= to) return `${index - 1}`
                          return locId
                        })

                        setCheckedRight(updatedCheckedRight)
                        onReorder(movedItem, from, to)
                      }}
                    >
                      {configRight.map(({ getValue, props, title }) => (
                        <TableCell {...props} data-cell-name={`${title}-cell`} key={`${item.id}-${title}`}>
                          <Typography component="div" variant="body2">
                            {getValue(item, i)}
                          </Typography>
                        </TableCell>
                      ))}
                    </DraggableAdCreativeRow>
                  ))}
                </TableBody>
              </MuiTable>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>No selected creatives.</Typography>
              </Box>
            )}
          </Box>
          <Stack direction={'row'} sx={{ flexShrink: 0 }}>
            <Box
              sx={{
                flexShrink: 0,
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ml: 2,
              }}
            >
              <Button
                disabled={checkedRight.length === 0}
                variant={'outlined'}
                onClick={() => {
                  onToggleFiller(adCreatives.filter((item) => checkedRight.includes(item.locId)))
                }}
              >
                Toggle Filler
              </Button>
            </Box>
            <Pagination
              total={adCreatives.length}
              page={selectedPagination.pageNumber}
              perPage={selectedPagination.rowsPerPage}
              changePageCallback={(page: string) => {
                setSelectedPagination((prev) => ({ ...prev, pageNumber: page }))
              }}
              changeRowsPerPageCallback={(rowsPerPage: string) => {
                setSelectedPagination((prev) => ({ ...prev, pageNumber: '0', rowsPerPage }))
              }}
              id="selected-ad-creatives-pagination"
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

export default TransferList
